import {
    FT5_ADD_ACTION_TO_EVENT_TRACKING,
    FT5_ADD_TO_EVENT_TRACKING,
    FT5_EVENT_TRACKING,
    FT5_MOVE_ACTION_TRACKING,
    FT5_MOVE_TRACKING,
    FT5_TRACK_ON_NEXT_PAGE_IMPRESSION,
} from '../../constants';
import { TrackingDataExtractor } from './tracking-data-extractor';
import { VariationIdEnhancer } from './variation-id-enhancer';
import {
    BCT_ADD_ACTION_TO_EVENT_TRACKING,
    BCT_TRACKING_ADD_TO_EVENT,
    BCT_TRACKING_SUBMIT_ACTION,
    BCT_TRACKING_SUBMIT_MOVE,
    BCT_TRACKING_SUBMIT_MOVE_ACTION,
    BCT_TRACK_ON_NEXT_PAGE_IMPRESSION,
} from '../../_common/client/bct-event-topics';
import { NamedTrackingFeatures } from './event-data-types';
import { VariationDataEnhancer } from './variation-data-enhancer';
import { Action, ActionName, DataContainer } from '@otto-ec/tracking-bct';
import { emitTrackingEvent } from '../../_common/client/tracking/emit-tracking-event';

const DATA_ATTRIBUTE_CLICK_TRACKING = 'data-ft5-click-tracking';

export class EventTracking {
    constructor(
        private trackingDataConverter: TrackingDataExtractor,
        private variationIdEnhancer: VariationIdEnhancer,
        private variationDataEnhancer: VariationDataEnhancer
    ) {}

    public delegateHtmlClickTracking(): void {
        const trackClickHandler = (event: MouseEvent) => {
            if (!(event.target instanceof Element)) {
                return;
            }
            this.trackWithParentsRecursive(event.target);
        };

        document.documentElement.addEventListener('click', trackClickHandler, { passive: true, capture: true });
    }

    public registerEventTrackingFromCode(): void {
        o_global.eventQBus.on(FT5_EVENT_TRACKING, (trackingData: DataContainer) => {
            emitTrackingEvent(BCT_TRACKING_SUBMIT_ACTION, this.variationIdEnhancer.enhance(trackingData), {
                name: 'click',
                features: this.variationDataEnhancer.enhance({}).features || [],
            });
        });

        o_global.eventQBus.on(FT5_MOVE_ACTION_TRACKING, (dataContainer: DataContainer, action: Action) => {
            const variationId = this.variationIdEnhancer.enhance({}).product_VariationId;
            if (variationId === undefined) {
                console.error('Could not retrieve variationId to add to move action');
                return;
            }
            emitTrackingEvent(
                BCT_TRACKING_SUBMIT_MOVE_ACTION,
                { ...dataContainer, product_VariationId: [variationId] },
                {
                    name: action.name,
                    features: action.features.concat(this.variationDataEnhancer.enhance({}).features || []),
                }
            );
        });

        o_global.eventQBus.on(FT5_MOVE_TRACKING, (trackingData: DataContainer) => {
            emitTrackingEvent(BCT_TRACKING_SUBMIT_MOVE, this.variationIdEnhancer.enhance(trackingData));
        });

        o_global.eventQBus.on(FT5_ADD_TO_EVENT_TRACKING, (trackingData: DataContainer, actionName: ActionName, eventMergeId: string) => {
            emitTrackingEvent(BCT_TRACKING_ADD_TO_EVENT, this.variationIdEnhancer.enhance(trackingData), eventMergeId);
            const additionalTrackingFeatures = this.variationDataEnhancer.enhance({}).features || [];
            if (additionalTrackingFeatures.length > 0) {
                emitTrackingEvent(
                    BCT_ADD_ACTION_TO_EVENT_TRACKING,
                    { name: actionName, features: additionalTrackingFeatures },
                    eventMergeId
                );
            }
        });

        o_global.eventQBus.on(FT5_ADD_ACTION_TO_EVENT_TRACKING, (data: NamedTrackingFeatures, eventMergeId: string) => {
            const additionalVariationFeatures = this.variationDataEnhancer.enhance({}).features || [];
            emitTrackingEvent(
                BCT_ADD_ACTION_TO_EVENT_TRACKING,
                { name: data.name, features: data.features.concat(additionalVariationFeatures) },
                eventMergeId
            );
        });

        o_global.eventQBus.on(FT5_TRACK_ON_NEXT_PAGE_IMPRESSION, (dataContainer: DataContainer) => {
            emitTrackingEvent(BCT_TRACK_ON_NEXT_PAGE_IMPRESSION, dataContainer);
        });
    }

    private trackClick(element: Element) {
        const trackingData = this.trackingDataConverter.extractTrackingData(element, DATA_ATTRIBUTE_CLICK_TRACKING);
        if (trackingData) {
            emitTrackingEvent(BCT_TRACKING_SUBMIT_ACTION, this.variationIdEnhancer.enhance(trackingData), {
                name: 'click',
                features: this.variationDataEnhancer.enhance({}).features || [],
            });
        }
    }

    /**
     * executes the {@link trackClick} method for the given element and all of its parents,
     * if those elements have tracking data attached as data attribute
     */
    private trackWithParentsRecursive(element: Element) {
        if (element.hasAttribute(DATA_ATTRIBUTE_CLICK_TRACKING)) {
            this.trackClick(element);
        }
        if (element.parentElement) {
            this.trackWithParentsRecursive(element.parentElement);
        }
    }
}
